import { format } from "date-fns";
import { css } from "styled-system/css";
import { useEffect, useMemo, useState } from "react";
import { Info } from "lucide-react";
import { AdminPageShell } from "@/components/admin/admin-page-shell";
import { AdminCostCharts } from "@/components/admin/admin-cost-charts";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { formatUsd } from "@/lib/format-usd";
import { enumerateMonths } from "@/lib/month-range";
import { Badge, type BadgeTone } from "@/components/ui/badge";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { EmptyState } from "@/components/admin/empty-state";

type PlanType = "FREE" | "PRO" | "ENTERPRISE";

type CostRow = {
  professionalId: string;
  slug: string;
  plan: PlanType;
  waSent: number;
  emailSent: number;
  waFailed: number;
  waCostUsd: number;
  emailCostUsd: number;
  totalCostUsd: number;
};

type MonthlyCostRow = {
  month: string;
  professionalId: string;
  slug: string;
  waSent: number;
  emailSent: number;
  waCostUsd: number;
  emailCostUsd: number;
  totalCostUsd: number;
};

/** Umbral para resaltar agendas con costo alto en el rango. */
const COST_ALERT_THRESHOLD_USD = 10;

const PLAN_LABELS: Record<PlanType, string> = { FREE: "Free", PRO: "Pro", ENTERPRISE: "Enterprise" };
const PLAN_TONES: Record<PlanType, BadgeTone> = { FREE: "neutral", PRO: "success", ENTERPRISE: "warning" };

export function AdminCosts() {
  const { refresh } = useAuth();
  const currentMonth = format(new Date(), "yyyy-MM");
  const [from, setFrom] = useState(currentMonth);
  const [to, setTo] = useState(currentMonth);
  const [rows, setRows] = useState<CostRow[] | null>(null);
  const [monthlyRows, setMonthlyRows] = useState<MonthlyCostRow[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setMonthlyRows(null);
    void (async () => {
      const qs = new URLSearchParams({ from, to }).toString();
      const [totalsRes, monthlyRes] = await Promise.all([
        fetchWithSessionSync(`/api/admin/metrics/costs?${qs}`, { credentials: "include" }, refresh),
        fetchWithSessionSync(`/api/admin/metrics/costs/monthly?${qs}`, { credentials: "include" }, refresh),
      ]);
      if (cancelled) return;
      setRows(totalsRes.ok ? ((await totalsRes.json()) as CostRow[]) : []);
      setMonthlyRows(monthlyRes.ok ? ((await monthlyRes.json()) as MonthlyCostRow[]) : []);
    })();
    return () => {
      cancelled = true;
    };
  }, [from, to, refresh]);

  const totals = (rows ?? []).reduce(
    (acc, r) => ({
      waSent: acc.waSent + r.waSent,
      emailSent: acc.emailSent + r.emailSent,
      waCostUsd: acc.waCostUsd + r.waCostUsd,
      emailCostUsd: acc.emailCostUsd + r.emailCostUsd,
      totalCostUsd: acc.totalCostUsd + r.totalCostUsd,
    }),
    { waSent: 0, emailSent: 0, waCostUsd: 0, emailCostUsd: 0, totalCostUsd: 0 },
  );

  const months = useMemo(() => enumerateMonths(from, to), [from, to]);

  const { waCostByMonth, emailCostByMonth, accountSeries } = useMemo(() => {
    const monthIndex = new Map(months.map((m, i) => [m, i]));
    const wa = months.map(() => 0);
    const email = months.map(() => 0);
    const bySlug = new Map<string, number[]>();

    for (const row of monthlyRows ?? []) {
      const idx = monthIndex.get(row.month);
      if (idx === undefined) continue;
      wa[idx] += row.waCostUsd;
      email[idx] += row.emailCostUsd;
      const series = bySlug.get(row.slug) ?? months.map(() => 0);
      series[idx] += row.totalCostUsd;
      bySlug.set(row.slug, series);
    }

    const accountSeries = Array.from(bySlug.entries())
      .map(([slug, dataByMonth]) => ({ slug, dataByMonth }))
      .sort((a, b) => a.slug.localeCompare(b.slug));

    return { waCostByMonth: wa, emailCostByMonth: email, accountSeries };
  }, [months, monthlyRows]);

  const activeAgendasCount = rows?.length ?? 0;
  const avgMonthlyCostPerAgenda =
    activeAgendasCount > 0 ? totals.totalCostUsd / months.length / activeAgendasCount : 0;

  const thClass = css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted", whiteSpace: "nowrap" });
  const tdClass = css({ py: "3", px: "4", fontSize: "sm" });

  return (
    <AdminPageShell
      title="Costos"
      description="Costo estimado de notificaciones (WhatsApp + email) por agenda para el rango de meses seleccionado, según el pricing de Twilio/SendGrid."
    >
      <div
        className={css({
          mb: "4",
          p: "3",
          rounded: "lg",
          borderWidth: "1px",
          borderColor: "border.DEFAULT",
          bg: "bg.card",
          display: "flex",
          alignItems: "center",
          gap: "3",
        })}
      >
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "fg.muted" })}>Desde</span>
          <input
            type="month"
            value={from}
            onChange={(e) => e.target.value && setFrom(e.target.value)}
            className={css({ h: "9", px: "3", rounded: "md", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "white", fontSize: "sm" })}
          />
        </label>
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "fg.muted" })}>Hasta</span>
          <input
            type="month"
            value={to}
            onChange={(e) => e.target.value && setTo(e.target.value)}
            className={css({ h: "9", px: "3", rounded: "md", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "white", fontSize: "sm" })}
          />
        </label>
      </div>

      {rows === null || monthlyRows === null ? (
        <LoadingFrame variant="light" minHeight={320} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Sin envíos en el rango seleccionado"
          description="Cuando haya WhatsApp o emails enviados en los meses seleccionados, el costo estimado por agenda va a aparecer acá."
          icon="💸"
        />
      ) : (
        <>
          <AdminCostCharts
            monthLabels={months}
            waCostByMonth={waCostByMonth}
            emailCostByMonth={emailCostByMonth}
            accountSeries={accountSeries}
            pieLabels={rows.map((r) => r.slug)}
            pieData={rows.map((r) => r.totalCostUsd)}
          />

          <div
            className={css({
              mb: "6",
              p: "4",
              rounded: "lg",
              borderWidth: "1px",
              borderColor: "#c7d2fe",
              bg: "#eef2ff",
              display: "flex",
              alignItems: "flex-start",
              gap: "3",
            })}
          >
            <Info size={18} className={css({ color: "#4338ca", flexShrink: 0, mt: "0.5" })} aria-hidden />
            <p className={css({ fontSize: "sm", color: "#3730a3", lineHeight: "1.6" })}>
              En el período seleccionado, el costo total estimado fue <b>{formatUsd(totals.totalCostUsd)}</b> USD.
              {" "}El costo promedio mensual por agenda activa fue <b>{formatUsd(avgMonthlyCostPerAgenda)}</b> USD.
            </p>
          </div>

          <div className={css({ overflowX: "auto", rounded: "xl", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "bg.card" })}>
            <table className={css({ w: "full", borderCollapse: "collapse", fontSize: "sm" })}>
              <thead>
                <tr className={css({ bg: "bg.muted", textAlign: "left" })}>
                  <th className={thClass}>Professional</th>
                  <th className={thClass}>Plan</th>
                  <th className={thClass}>WA enviados</th>
                  <th className={thClass}>Emails enviados</th>
                  <th className={thClass}>Costo WA (USD)</th>
                  <th className={thClass}>Costo Email (USD)</th>
                  <th className={thClass}>Costo total (USD)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isHigh = r.totalCostUsd > COST_ALERT_THRESHOLD_USD;
                  return (
                    <tr
                      key={r.professionalId}
                      className={css({
                        borderTopWidth: "1px",
                        borderColor: isHigh ? "red.200" : "border.DEFAULT",
                        bg: isHigh ? "red.50" : "transparent",
                        _hover: { bg: isHigh ? "red.100" : "bg.muted" },
                      })}
                    >
                      <td className={css({ py: "3", px: "4", fontSize: "sm", fontWeight: "medium", color: "fg.DEFAULT" })}>{r.slug}</td>
                      <td className={tdClass}>
                        <Badge tone={PLAN_TONES[r.plan]}>{PLAN_LABELS[r.plan]}</Badge>
                      </td>
                      <td className={tdClass}>{r.waSent.toLocaleString("es-AR")}</td>
                      <td className={tdClass}>{r.emailSent.toLocaleString("es-AR")}</td>
                      <td className={tdClass}>{formatUsd(r.waCostUsd)}</td>
                      <td className={tdClass}>{formatUsd(r.emailCostUsd)}</td>
                      <td
                        className={css({
                          py: "3",
                          px: "4",
                          fontSize: "sm",
                          fontWeight: "bold",
                          color: isHigh ? "red.700" : "fg.DEFAULT",
                        })}
                      >
                        {formatUsd(r.totalCostUsd)}
                        {isHigh && (
                          <span className={css({ fontSize: "xs", fontWeight: "normal", color: "red.600", ml: "1.5" })}>
                            {`> $${COST_ALERT_THRESHOLD_USD}`}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className={css({ borderTopWidth: "2px", borderColor: "border.DEFAULT", bg: "bg.muted" })}>
                  <td className={css({ py: "3", px: "4", fontSize: "sm", fontWeight: "bold", color: "fg.DEFAULT" })} colSpan={2}>
                    Total ({rows.length} {rows.length === 1 ? "agenda" : "agendas"})
                  </td>
                  <td className={css({ py: "3", px: "4", fontSize: "sm", fontWeight: "bold" })}>{totals.waSent.toLocaleString("es-AR")}</td>
                  <td className={css({ py: "3", px: "4", fontSize: "sm", fontWeight: "bold" })}>{totals.emailSent.toLocaleString("es-AR")}</td>
                  <td className={css({ py: "3", px: "4", fontSize: "sm", fontWeight: "bold" })}>{formatUsd(totals.waCostUsd)}</td>
                  <td className={css({ py: "3", px: "4", fontSize: "sm", fontWeight: "bold" })}>{formatUsd(totals.emailCostUsd)}</td>
                  <td className={css({ py: "3", px: "4", fontSize: "sm", fontWeight: "bold" })}>{formatUsd(totals.totalCostUsd)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </AdminPageShell>
  );
}
